import React, { useMemo } from 'react'
import { Col, Row } from 'react-bootstrap'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import { ClaimHistory as ClaimHistoryType } from '@dataTypes/insurance.types'
import { filteredClaimsData } from '@utils/claims.util'
import { formatDate } from '@utils/date.utils'
import StatusBadge, {
  StatusTypes,
} from '@components/common/StatusBadge/StatusBadge.component'
import InsuranceEmptyCard from '@components/pages/Insurance/InsuranceEmptyCard/InsuranceEmptyCard.component'
import { TextAlign } from '@syncfusion/ej2-react-grids'

interface ClaimHistoryProps {
  historyData: ClaimHistoryType[]
  outlined?: boolean
  onSubmitClaim?: () => void
}

const isFullClaimRow = (row: unknown): boolean =>
  Boolean(
    row &&
      typeof row === 'object' &&
      Array.isArray((row as { claimitems?: unknown }).claimitems),
  )

const formatClaimHistoryDate = (row: Record<string, unknown>): string => {
  const raw = row.createdAt ?? row.claim_date
  if (typeof raw !== 'string' || raw.length === 0) return '—'
  const formatted = formatDate(raw)
  return formatted.includes('NaN') ? raw : formatted
}

const getClaimHistoryRows = (historyData: ClaimHistoryType[]): unknown[] => {
  if (!Array.isArray(historyData) || historyData.length === 0) return []

  if (historyData.every(isFullClaimRow)) {
    try {
      return (
        filteredClaimsData({ claims: historyData })?.filter(
          (claim: { generalStatus?: string }) =>
            claim.generalStatus === 'Approved' ||
            claim.generalStatus === 'Partially Approved',
        ) ?? []
      )
    } catch {
      return []
    }
  }

  return historyData
}

const ClaimHistory: React.FC<ClaimHistoryProps> = ({
  historyData,
  outlined = false,
  onSubmitClaim,
}) => {
  const rows = useMemo(() => getClaimHistoryRows(historyData), [historyData])

  const titleRow = (
    <Row>
      <Col>
        <div>
          <p className="section-title">Claims history</p>
        </div>
      </Col>
    </Row>
  )

  const columns = [
    {
      field: 'space',
      headerText: '',
      width: '10px',
    },
    {
      field: 'id',
      headerText: 'Claim ID',
      width: '160px',
      template: (row: Record<string, unknown>) => (
        <span className="claim-id-text">
          {(row.providerClaimId as string) ??
            (row.public_id as string) ??
            (row.claim_id as string) ??
            (row.id as string) ??
            '—'}
        </span>
      ),
    },
    {
      field: 'createdAt',
      headerText: 'Date',
      width: '160px',
      template: (row: Record<string, unknown>) => (
        <span>{formatClaimHistoryDate(row)}</span>
      ),
    },
    {
      field: 'claimAmount',
      headerText: 'Claim amount',
      width: '160px',
      template: (row: Record<string, unknown>) => (
        <span>
          {(row.claimAmount as string) ?? (row.claim_amount as string) ?? '—'}
        </span>
      ),
    },
    {
      field: 'amountPaid',
      headerText: 'Approved ins.',
      width: '160px',
      template: (row: Record<string, unknown>) => (
        <span>{(row.amountPaid as string) ?? '—'}</span>
      ),
    },
    {
      field: 'status',
      headerText: 'Status',
      width: '200px',
      textAlign: 'Left' as TextAlign,
      template: (row: Record<string, unknown>) => {
        const status = (row.generalStatus ?? row.status) as
          | StatusTypes
          | undefined
        if (!status) return <span>—</span>
        return <StatusBadge status={status} type="claims" />
      },
    },
  ]

  if (rows.length === 0) {
    return (
      <div className="section-claim-history">
        {titleRow}
        <div className="section-claim-history--empty">
          <InsuranceEmptyCard
            iconClass="mgc mgc_receive_money_fill"
            emptyTitle="No claims found"
            description="You don't have any claim history on record yet."
            actionText="Submit claim"
            onAction={onSubmitClaim}
            outlined={outlined}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="section-claim-history">
      {titleRow}
      <Row>
        <Col md={12}>
          <div className="grid-container">
            <DataGridComponent
              data={rows}
              columns={columns}
              rowHeight={36}
              allowPaging={true}
              allowSorting={true}
              pageSize={10}
            />
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default ClaimHistory
